// Каталог ассетов: всё, что игра грузит из /assets.
//
// Здесь перечислены адреса файлов, нужные кадры листов, нужные анимации скелетов и нужные
// звуки атласов. Картинки грузит менеджер ассетов, звук — свой движок, см. sound/engine.ts.
// Ключи кадров записаны так, как они лежат в JSON. Имя папки внутри ключа не совпадает
// с именем файла у части листов — например, у misc-ui.json это «ui», — поэтому ключ
// указывается целиком, а не собирается по правилу.
//
// Внизу файла ассеты собраны в наборы: набор — это то, что грузится за один шаг загрузки.
//
// Тест tests/assets/catalog.test.ts сверяет каждую запись с файлами на диске.

import type { SoundSpriteSource } from '../sound/engine';

import type { AssetBundle, FontSource, SpineSkeletonSource, TextureSheetSource } from './manager';

/** Экран загрузки: логотип студии и полоса прогресса из четырёх слоёв. */
export const PRELOADER_SHEET = {
  url: '/images/preloader/preloader.json',
  frames: {
    studioLogo: 'preloader/logo.png',
    progressBack: 'preloader/jr_ss_preloader_back.png',
    progressFill: 'preloader/jr_ss_preloader.png',
    progressFrame: 'preloader/jr_ss_preloader_frame.png',
    progressMask: 'preloader/jr_ss_preloader_mask.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа экрана загрузки. */
export type PreloaderFrame = keyof typeof PRELOADER_SHEET.frames;

/** Символы на остановленных барабанах. */
export const SYMBOLS_SHEET = {
  url: '/images/symbols/symbols.json',
  frames: {
    bell: 'symbols/jr_ss_high_2_bell_st.png',
    cherry: 'symbols/jr_ss_low_cherry_st.png',
    goldBars: 'symbols/jr_ss_high_1_goldbars_st.png',
    hat: 'symbols/jr_ss_high_3_hat_st.png',
    lemon: 'symbols/jr_ss_low_lemon_st.png',
    pear: 'symbols/jr_ss_low_pear_st.png',
    plum: 'symbols/jr_ss_low_plum_st.png',
    scatter: 'symbols/jr_ss_special_symbol_st.png',
    suitcase: 'symbols/jr_ss_high_4_suitcase_st.png',
    wild: 'symbols/jr_ss_wild_st.png',
    wildMultiplier: 'symbols/jr_ss_wild_multiplier_st.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа символов. */
export type SymbolFrame = keyof typeof SYMBOLS_SHEET.frames;

/**
 * Символы на крутящихся барабанах.
 *
 * Ключи взяты по тому, что нарисовано на кадре, а не по имени файла: на кадре
 * jr_ss_wild_multiplier_blurred нарисована буква W, то есть обычный Wild, а не манометр.
 * Кадра с манометром в листе нет, потому что Wild Multiplier в полосы барабанов не входит.
 */
export const BLURRED_SYMBOLS_SHEET = {
  url: '/images/symbols_blured/symbols_blur.json',
  frames: {
    bell: 'symbols_blur/jr_ss_high_blurred_2.png',
    cherry: 'symbols_blur/jr_ss_low_cherry_st.png',
    goldBars: 'symbols_blur/jr_ss_high_blurred_1.png',
    hat: 'symbols_blur/jr_ss_high_3_hat_st.png',
    lemon: 'symbols_blur/jr_ss_low_lemon_st.png',
    pear: 'symbols_blur/jr_ss_low_pear_st.png',
    plum: 'symbols_blur/jr_ss_low_plum_st.png',
    scatter: 'symbols_blur/jr_ss_special_symbol_blurred.png',
    suitcase: 'symbols_blur/jr_ss_high_4_suitcase_st.png',
    wild: 'symbols_blur/jr_ss_wild_multiplier_blurred.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа размытых символов. */
export type BlurredSymbolFrame = keyof typeof BLURRED_SYMBOLS_SHEET.frames;

/**
 * Кнопки игры.
 *
 * Кнопка спина нарисована тремя кадрами: обычным, под курсором и нажатым. В листе есть вторая
 * такая же тройка spin_standart/over/press, но стрелка на ней идёт против часовой стрелки.
 * Взята тройка с приставкой JR: это правленые кадры под эту игру.
 *
 * Кнопка скипа — тот же круг с золотым квадратом внутри. В листе её кадры названы stop,
 * но в игре она обрывает выдержку перед остановкой барабанов, а не останавливает автоспин.
 * Кадр у неё того же размера, что у спина, поэтому обе кнопки встают на одно место.
 *
 * Кнопка ok — широкая плашка 330x192. Игра ставит её в сообщение об ошибке связи.
 *
 * Кнопки info и help — круги 84x84 с буквой i и знаком вопроса. Ими игрок открывает таблицу
 * выплат и правила игры, см. ui/help.ts. Кадров у каждой три, как и у кнопки спина.
 *
 * Кнопка звука — значок 100x100 с волнами и он же перечёркнутый, см. ui/soundButton.ts. Кадра
 * у неё два, по одному на состояние: наведение и нажатие своих кадров у этого значка нет.
 * В листе лежат белый значок и чёрный, взят белый: кнопка стоит на тёмном фоне игры.
 *
 * Кнопка полного экрана — четыре золотых уголка в кадре 190x190, кадров три. Кадр вдвое крупнее
 * соседей по ряду, поэтому игра ужимает его до их размера, см. ui/fullscreenButton.ts.
 */
export const UI_SHEET = {
  url: '/images/game-ui/ui.json',
  frames: {
    spinIdle: 'ui/JR_spin_button_standart_fix.png',
    spinHover: 'ui/JR_spin_button_over_fix.png',
    spinActive: 'ui/JR_spin_button_press_fix.png',
    skipIdle: 'ui/stop_standart.png',
    skipHover: 'ui/stop_over.png',
    skipActive: 'ui/stop_press.png',
    autoplayPlayIdle: 'ui/JR_autospin_play_button_standart100.png',
    autoplayPlayHover: 'ui/JR_autospin_play_button_over100.png',
    autoplayPlayActive: 'ui/JR_autospin_play_button_press100.png',
    autoplayStopIdle: 'ui/JR_autospin_stop_button_standart100.png',
    autoplayStopHover: 'ui/JR_autospin_stop_button_over100.png',
    autoplayStopActive: 'ui/JR_autospin_stop_button_press100.png',
    okIdle: 'ui/button_ok_standart.png',
    okHover: 'ui/button_ok_over.png',
    okActive: 'ui/button_ok_press.png',
    infoIdle: 'ui/info_standart.png',
    infoHover: 'ui/info_over.png',
    infoActive: 'ui/info_press.png',
    helpIdle: 'ui/question_standart.png',
    helpHover: 'ui/question_over.png',
    helpActive: 'ui/question_press.png',
    fullscreenIdle: 'ui/full_screen_button_standart.png',
    fullscreenHover: 'ui/full_screen_button_over.png',
    fullscreenActive: 'ui/full_screen_button_press.png',
    soundOn: 'ui/sound_on_white_100.png',
    soundOff: 'ui/sound_off_white_100.png',
    settingsOff: 'ui/settings_off.png',
    settingsOn: 'ui/settings_on.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа интерфейса. */
export type UiFrame = keyof typeof UI_SHEET.frames;

/**
 * Плашка кнопки на створках заглушки поля и части нижней полосы, см. GDD, раздел 7.2.
 *
 * Плашка 396x154 нарисована под медную рамку створки: ею игрок торопит показ бонуса
 * и закрывает поздравление. Кадров в листе четыре, игре нужны три. Наведённый кадр назван
 * там active, а нажатый — pressed, поэтому имена в коде взяты по виду кнопки, а не по файлу.
 *
 * Остальное — нижняя полоса: тень во всю ширину 1442x132, плашки счёта и выигрыша 223x53,
 * плашка ставки 189x53 и круглые кнопки ставки. У кнопок ставки наведённый кадр крупнее
 * обычного, 105 против 88: кнопка растёт под курсором и растёт от своей середины.
 *
 * Все три поля полосы стоят на плашках одного вида. Рамка ставки frame_stake 271x107 залита
 * кремовым, и светлая сумма на ней не читается, поэтому взята тёмная плашка back_stake.
 *
 * Крест закрывает попап, см. ui/popup.ts. Наведённый кадр у него крупнее обычного, 128 против
 * 96, — как и у кнопок ставки: крест растёт под курсором и растёт от своей середины.
 */
export const MISC_UI_SHEET = {
  url: '/images/game-ui/misc-ui.json',
  frames: {
    closeIdle: 'ui/cross_normal.png',
    closeHover: 'ui/cross_active.png',
    closeActive: 'ui/cross_pressed.png',
    plateIdle: 'ui/jr_ss_button_normal.png',
    plateHover: 'ui/jr_ss_button_active.png',
    plateActive: 'ui/jr_ss_button_pressed.png',
    barShadow: 'ui/back_shadow.png',
    barBalance: 'ui/back_balance.png',
    barLastWin: 'ui/back_last_win.png',
    barStake: 'ui/back_stake.png',
    stakeUpIdle: 'ui/botton_stake+_normal.png',
    stakeUpHover: 'ui/botton_stake+_active.png',
    stakeUpActive: 'ui/botton_stake+_pressed.png',
    stakeDownIdle: 'ui/botton_stake-_normal.png',
    stakeDownHover: 'ui/botton_stake-_active.png',
    stakeDownActive: 'ui/botton_stake-_pressed.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа плашек. */
export type MiscUiFrame = keyof typeof MISC_UI_SHEET.frames;

/**
 * Листалка страниц попапа: стрелка 98x152 и точка страницы 64x63.
 *
 * Стрелка нарисована влево, поэтому правую игра отражает по горизонтали, см. ui/popup.ts.
 * Кадров у неё четыре: обычный, под курсором, нажатый и погашенный. Погашенный кадр художник
 * дал сам, поэтому крайняя страница гасит стрелку кадром, а не прозрачностью.
 *
 * Лист лежит в папке splash: художник рисовал листалку под галерею скриншотов.
 */
export const PAGER_SHEET = {
  url: '/images/splash/splash.json',
  frames: {
    arrowIdle: 'splash/normal_arrow.png',
    arrowHover: 'splash/active_arrow.png',
    arrowActive: 'splash/pressed_arrow.png',
    arrowBlocked: 'splash/disabled_arrow.png',
    pageOn: 'splash/active_page.png',
    pageOff: 'splash/inactive_page.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Имена кадров листа листалки. */
export type PagerFrame = keyof typeof PAGER_SHEET.frames;

/**
 * Шрифт суммы выигрыша: золотые цифры и точка между целой и дробной частью.
 *
 * Кадры в листе названы по коду символа в ASCII: 48 — ноль, 57 — девятка, 46 — точка,
 * 44 — запятая. Имя кадра в коде — сам символ, поэтому сумма собирается прямо по своей записи.
 * В листе лежат ещё пробел, плюс и косая черта, игре они пока не нужны.
 *
 * Все кадры шрифта одной высоты, поэтому цифры и точка встают на строку без правки.
 *
 * Имени кадров у шрифта нет: строка набирается по своей записи, символ за символом,
 * см. ui/amount.ts. Поэтому и типа имён кадров у него нет, в отличие от прочих листов.
 */
export const WIN_FONT_SHEET = {
  url: '/images/winFont/winFont.json',
  frames: {
    '0': 'win/48.png',
    '1': 'win/49.png',
    '2': 'win/50.png',
    '3': 'win/51.png',
    '4': 'win/52.png',
    '5': 'win/53.png',
    '6': 'win/54.png',
    '7': 'win/55.png',
    '8': 'win/56.png',
    '9': 'win/57.png',
    '.': 'win/46.png',
    ',': 'win/44.png',
  },
} as const satisfies TextureSheetSource<string>;

/** Все объявленные листы. По этому списку тест проверяет, что кадры существуют. */
export const ALL_TEXTURE_SHEETS: readonly TextureSheetSource<string>[] = [PRELOADER_SHEET, SYMBOLS_SHEET, BLURRED_SYMBOLS_SHEET, UI_SHEET, MISC_UI_SHEET, PAGER_SHEET, WIN_FONT_SHEET];

/** Фон стоящего поезда: гора слева, станция справа, путь между ними. */
export const BACKGROUND_IMAGE: string = '/images/background/BG.jpg';

/**
 * Фон идущего поезда: тот же каньон размытым дальним планом.
 *
 * Ни горы, ни станции на нём нет: на ходу они едут отдельными картинками и уходят вдаль,
 * см. MOUNTAIN_IMAGE и STATION_IMAGE.
 */
export const RUSH_BACKGROUND_IMAGE: string = '/images/background/BG1.jpg';

/** Гора слева. На стоящем фоне она нарисована, на ходу уезжает этой картинкой. */
export const MOUNTAIN_IMAGE: string = '/images/background/mount.png';

/** Станция справа. Она уходит и возвращается тем же порядком, что и гора. */
export const STATION_IMAGE: string = '/images/background/RST.png';

/**
 * Место картинки фона в координатах скелета: сетка слота BG и ось пути на ней.
 *
 * Фон рисует скелет, а не сама картинка: он же уводит гору со станцией вдаль и гонит землю
 * мимо машины, см. BACKGROUND_SKELETON. Картинка BG.jpg 1950x2003 лежит в нём сеткой слота BG,
 * и по этой сетке фон вписывается в экран.
 *
 * Путь нарисован правее середины картинки: на 1023 пикселе из 1950, то есть на 2.85 в единицах
 * скелета — почти в его начале координат. По этой оси фон и выравнивается, иначе путь уходит
 * вбок от машины.
 */
export const BACKGROUND_PLATE = { left: -1499.55, right: 1364.42, bottom: -1454.45, top: 1487.35, axisX: 2.85 } as const;

/** Барабаны: рамка машины с полем 3x3 и бонусным рядом 1x3 над ним. */
export const REELS_IMAGE: string = '/images/reels/jr_ss_reels_final.png';

/**
 * Сама машина внутри квадрата 1920x1920.
 *
 * Прямоугольник снят по пикселям с альфой от 128: тень справа и снизу в него не входит.
 * По нему считаются центр и масштаб поля. По всей непрозрачной части картинки машина
 * встала бы левее центра экрана, потому что тень тянет прямоугольник вправо.
 */
export const REELS_CONTENT = { x: 436, y: 153, width: 1047, height: 1642 } as const;

/**
 * Сетка окон в координатах картинки барабанов.
 *
 * Колонки общие у поля 3x3 и у бонусного ряда 3x1 над ним: центр, шаг и размер окна те же.
 * Ряды свои: у поля их три, у бонусного ряда один.
 *
 * Снято по перемычкам между окнами: по горизонтали они стоят на 834..854 и 1061..1080,
 * по вертикали у поля — на 1036 и 1206, у бонусного ряда окно занимает 457..626.
 * Окно совпадает с кадром символа 204x170, запаса нет, поэтому ошибка в шаге сразу видна
 * на крайних окнах.
 *
 * Циферблаты манометров нарисованы на рамке между бонусным рядом и полем, по одному
 * на колонку, см. GDD, раздел 5.1. Стрелка ходит вокруг оси, а ось помечена на циферблате
 * точкой: по горизонтали она стоит под центральным делением шкалы, по вертикали — на уровне
 * крайних. Циферблаты нарисованы по одному на колонку, но стоят не строго по её середине,
 * поэтому оси сняты по пикселям с самой картинки, а не считаются по колонкам.
 *
 * Размер манометра — это круг безеля, он занимает 648..815 по горизонтали и 653..822
 * по вертикали. По нему берётся масштаб стрелки: скелет нарисован под сам циферблат.
 *
 * Радиус угла снят с картинки маски окон jr_ss_reels_mask_1.png: её скругление доходит
 * до края на 30 пикселях от угла. По нему игра рисует маску сама, см. ui/reels.ts.
 */
export const REELS_GRID = {
  centerX: 957,
  stepX: 225.5,
  windowWidth: 204,
  windowHeight: 170,
  cornerRadius: 30,
  main: { centerY: 1121, stepY: 170.5, rows: 3 },
  bonus: { centerY: 541.5 },
  gauge: {
    size: 167,
    pivots: [
      { x: 723, y: 732.5 },
      { x: 956, y: 732 },
      { x: 1182, y: 731 },
    ],
  },
} as const;

/** Доска попапа: тёмно-синий лист с медной рамкой и кольцами блокнота сверху. */
export const POPUP_BOARD_IMAGE: string = '/images/popups/buy_feature_popup.png';

/** Заголовок попапа: медная плашка с завитками. Она ложится на верх доски. */
export const POPUP_TITLE_IMAGE: string = '/images/popups/buy_feature_popup_top.png';

/**
 * Как доска попапа режется по высоте.
 *
 * Художник нарисовал её под покупку фичи: 909x856 с двумя завитками поперёк, на 324..340
 * и на 591..607. Страницу текста эти завитки перечёркивают, поэтому игра собирает доску
 * из трёх кусков и берёт из картинки только то, что между ними.
 *
 * Верх — строки 0..250: кольца, верхняя линия рамки и угловые завитки, они кончаются на 161.
 * Середина — строки 250..300: голубая заливка с боковыми линиями рамки, её игра растягивает
 * на нужную высоту. Низ — строки 620..856: заливка, нижние угловые завитки на 736..792
 * и нижняя линия рамки.
 *
 * Куски стыкуются по заливке, поэтому шва не видно, а доска выходит любой высоты.
 */
export const POPUP_BOARD = {
  width: 909,
  head: 250,
  stretch: { y: 250, height: 50 },
  foot: { y: 620, height: 236 },
} as const;

/** Девять линий выплат, каждая на своей картинке поля. */
export const PAYLINES_IMAGE: string = '/images/paytable/jr_ss_llines.png';

/** Wild на среднем барабане: с него начинается Nudge Respin. */
export const RESPIN_IMAGE: string = '/images/paytable/jr_ss_re_spin_feature.png';

/** Wild multiplier заряжает манометр своего барабана. */
export const CHARGE_IMAGE: string = '/images/paytable/jr_ss_ss_bonus_2.jpg';

/** Полные манометры и общий множитель на счётчике: так начинается бонус. */
export const BONUS_IMAGE: string = '/images/paytable/jr_ss_ss_bonus_meter.jpg';

/** Три скаттера на поле: они запускают Red Hot Spin. */
export const SCATTER_IMAGE: string = '/images/paytable/jr_ss_ss_scater.png';

/** Медный завиток под заголовком страницы попапа. */
export const DIVIDER_IMAGE: string = '/images/paytable/line_paytable.png';

/** Все объявленные картинки. По этому списку тест проверяет, что файлы существуют. */
export const ALL_IMAGES: readonly string[] = [
  BACKGROUND_IMAGE,
  RUSH_BACKGROUND_IMAGE,
  MOUNTAIN_IMAGE,
  STATION_IMAGE,
  REELS_IMAGE,
  POPUP_BOARD_IMAGE,
  POPUP_TITLE_IMAGE,
  PAYLINES_IMAGE,
  RESPIN_IMAGE,
  CHARGE_IMAGE,
  BONUS_IMAGE,
  SCATTER_IMAGE,
  DIVIDER_IMAGE,
];

/**
 * Шрифт надписей поверх поля.
 *
 * В ассетах лежат пять шрифтов, для имён фич взят Roboto Slab: им набрана надпись
 * на референсе попапа Nudge Respin, см. GDD, раздел 7.2. Из пяти файлов шрифта игре нужен
 * один: woff2 понимают все браузеры, которые вообще тянут WebGL.
 */
export const TITLE_FONT = {
  url: '/fonts/Roboto-Slab/RobotoSlab-Regular.woff2',
  family: 'Roboto Slab',
} as const satisfies FontSource;

/**
 * Шрифт счётчика общего множителя.
 *
 * Число на карточке счётчика игра набирает сама: цифр в атласе панели нет вовсе, там лежит
 * образец художника, см. features/steamSpin/totalMultiplier.ts.
 */
export const COUNTER_FONT = {
  url: '/fonts/BebasNeue/BebasNeue-Regular.woff2',
  family: 'Bebas Neue',
} as const satisfies FontSource;

/** Все объявленные шрифты. По этому списку тест проверяет, что файлы существуют. */
export const ALL_FONTS: readonly FontSource[] = [TITLE_FONT, COUNTER_FONT];

/**
 * Музыка игры: две дорожки в одном файле.
 *
 * Main тянется весь обычный ход игры, free_spins — время бонуса, см. features/steamSpin.
 * Обе идут по кругу: дорожка кончается и начинается заново, пока её не сменят.
 *
 * Громкость взята вполсилы: музыка стоит за игрой, а не поверх неё, и эффекты спина
 * с выигрышем должны читаться сквозь неё.
 */
export const MUSIC_SPRITE = {
  url: '/sounds/music-sprite.json',
  looped: true,
  volume: 0.35,
  sounds: {
    main: 'music.main',
    freeSpins: 'music.free_spins',
  },
} as const satisfies SoundSpriteSource<string>;

/**
 * Звуковые эффекты игры.
 *
 * В атласе их тридцать шесть, игра зовёт четырнадцать: остальные ждут своих событий — перегрева
 * манометра, счётчика Big Win, паровоза на фоне. Объявлять звук, которого никто не играет,
 * незачем: атлас грузится целиком, а список в коде говорит, что игра умеет озвучивать.
 *
 * Имена в коде названы по событию игры, а не по файлу: nudge_respin_blinds — это створки
 * заглушки поля, и закрывает она не только респин, см. ui/fieldCover.ts.
 */
export const SFX_SPRITE = {
  url: '/sounds/sfx-sprite.json',
  volume: 0.7,
  sounds: {
    /** Нажатие любой кнопки игры, см. ui/button.ts. */
    button: 'sfx.button',

    /** Пуск барабанов оплаченного спина. */
    spin: 'sfx.spin',

    /** Остановка одного барабана. Барабаны встают очередью, и звук идёт за каждым. */
    reelStop: 'sfx.reel_stop_single',

    /**
     * Гудок паровоза. Им называется выигрыш, и другого повода подать голос у машины нет.
     *
     * Дорожка лежит в атласе под именем nudge_respin, но записан на ней голос машины: нота
     * 609 Гц целую секунду, а дальше своё затухание до тишины. Та же нота звучит и на дорожке
     * трогающегося паровоза, только там она тянется четыре секунды и обрывается на полном
     * ходу, откуда её ни режь. Поэтому взята эта: она кончается сама, см. ADR-50.
     *
     * Гудок идёт с паром из-под котла и порознь с ним не бывает, см. ADR-49.
     */
    trainWhistle: 'sfx.nudge_respin',

    /** Створки заглушки поля сходятся. */
    blindsClose: 'sfx.nudge_respin_blinds_close',

    /** Створки заглушки поля разъезжаются. */
    blindsOpen: 'sfx.nudge_respin_blinds_open',

    /** Рамка ожидания над последним барабаном, см. GDD, раздел 3.5. */
    anticipation: 'sfx.anticipation',

    /** Выигрыш до двух ставок, см. GDD, раздел 3.4. */
    winLow: 'sfx.win_low',

    /** Выигрыш до пяти ставок там же. */
    winMiddle: 'sfx.win_middle',

    /** Выигрыш до десяти ставок там же. */
    winHigh: 'sfx.win_high',

    /** Крупный выигрыш на створках, см. GDD, раздел 3.7. */
    bigWin: 'sfx.big_win_1',

    /**
     * Струя пара из крана машины, см. ui/steam.ts.
     *
     * Пар шипит, а не гудит: гудок паровоза обещает выигрыш, и звать его на открытую створку
     * мини-слота нельзя, см. ADR-49.
     *
     * Дорожка идёт шесть секунд ровным шипением и кончается на полном ходу. Взята секунда
     * с третью — ровно столько живёт сама струя, и хвост куска гасится сам, см. ADR-50.
     */
    steamJet: { key: 'sfx.big_win_steam_loop', seconds: 1.3 },

    /** Манометр принял заряд, см. GDD, раздел 4.2. */
    multiplierUp: 'sfx.multiplier_increase',

    /** Начало бонуса: манометры полны или на поле три скаттера, см. GDD, разделы 5.3 и 6. */
    bonusStart: 'sfx.scatter_trigger',

    /**
     * Машина подходит и тормозит, см. ui/arrival.ts.
     *
     * Дорожка идёт семь секунд, а подъезд — две. Первую секунду на ней паровоз только
     * подходит вдали, поэтому кусок берётся с неё: с 1.1 секунды слышен накат, на 2.6
     * визжат тормоза, дальше всё сходит на нет. Взято 3.1 секунды — это два клуба дыма
     * подряд, и кончают дым со звуком вместе, см. ui/smoke.ts.
     *
     * Кончается кусок на затухании, а не на полном ходу: обрывать тут нечего.
     */
    trainArrives: { key: 'sfx.train_stops', from: 1.1, seconds: 3.1 },

    /**
     * Паровоз трогается с места: за ним начинается поездка бонуса, см. ui/ride.ts.
     *
     * Дорожка идёт девять секунд и кончается на полном ходу, откуда её ни режь, поэтому она
     * взята целиком. Разгон на экране длиннее её, и остаток пути ведёт уже стук колёс.
     */
    trainStarts: 'sfx.train_starts',

    /**
     * Стук колёс на полном ходу. Идёт по кругу: конца у хода нет, кончает его прибытие.
     *
     * Дорожка тянется полминуты и кончается тишиной, поэтому круг не слышен стыком.
     */
    trainRuns: { key: 'sfx.train_moves', looped: true },

    /** Паровоз тормозит у станции: им кончается поездка бонуса. */
    trainStops: 'sfx.train_stops',

    /** Поздравление в конце бонуса, попап id_4 в GDD, раздел 7.2. */
    bonusEnd: 'sfx.any_bonus_end_popup',
  },
} as const satisfies SoundSpriteSource<string>;

/** Все объявленные атласы звуков. По этому списку тест сверяет их с файлами на диске. */
export const ALL_SOUND_SPRITES: readonly SoundSpriteSource<string>[] = [MUSIC_SPRITE, SFX_SPRITE];

/**
 * Логотип игры: паровоз с надписью Steam Spin и дымом из трубы.
 *
 * Анимации в файле названы animation2 и animation3, разница между ними одна: только animation3
 * крутит кадры дыма. Вторая игре не нужна, поэтому не объявлена.
 */
export const LOGO_SKELETON = {
  skeletonUrl: '/animations/jr_ss_logo/jr_ss_logo.json',
  atlasUrl: '/animations/jr_ss_logo/jr_ss_logo.atlas',
  animations: {
    smoke: 'animation3',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Разделитель между барабанами: медная полоса, по которой едут зубцы.
 *
 * Полоса занимает 24x554 вокруг начала координат скелета и ровно закрывает промежуток
 * между окнами поля. Анимацию надо держать включённой: зубцы стоят на пути, а без анимации
 * путь не считается, и они уезжают вбок от полосы. Габарит в заголовке скелета (38x555)
 * снят как раз с такой позы, поэтому по нему разделитель ставить нельзя.
 */
export const REEL_GEAR_SKELETON = {
  skeletonUrl: '/animations/reels/jr_ss_reel_gear/jr_ss_reel_gear.json',
  atlasUrl: '/animations/reels/jr_ss_reel_gear/jr_ss_reel_gear.atlas',
  animations: {
    move: 'animation',
  },
} as const satisfies SpineSkeletonSource<string>;

/** Габарит полосы разделителя вокруг начала координат скелета, снят на ходу анимации. */
export const REEL_GEAR_SIZE = { width: 24, height: 554 } as const;

/**
 * Заглушка поля 3x3: две медные створки, которые разъезжаются вверх и вниз.
 *
 * Закрытая заглушка занимает 659x513 — ровно окна поля. Открытое состояние idle_open
 * прячет створки, но оставляет мелкие детали посреди поля, поэтому игра его не показывает:
 * после open поза держится сама.
 *
 * Слоты title_cube_1 и title_cube_2 держат чёрный квадрат 61x57 — заготовку художника
 * под надпись и кнопку. Они висят на костях створок и едут вместе с ними, а убрать их
 * не может ни одна анимация: ключей attachment у этих слотов нет нигде. Поэтому игра
 * снимает их сама. Такие же квадраты лежат в скелетах anticipation и win, их черёд придёт.
 *
 * Слот root держит обрезку по рамке поля. Spine вешает её маской на узлы слотов, поэтому
 * и надпись, и кнопка обрезаются краем поля, см. REEL_COVER_CAPTION.
 */
export const REEL_COVER_SKELETON = {
  skeletonUrl: '/animations/reels/jr_ss_reel_3x3/jr_ss_reels_3x3.json',
  atlasUrl: '/animations/reels/jr_ss_reel_3x3/jr_ss_reels_3x3.atlas',
  animations: {
    open: 'open',
    close: 'close',
  },
  hiddenSlots: ['title_cube_1', 'title_cube_2'],
} as const satisfies SpineSkeletonSource<string>;

/**
 * Место надписи на заглушке поля.
 *
 * Игра ставит надпись туда, где художник оставил под неё заготовку: слот title_cube_2
 * на верхней створке. Сам квадрат заготовки снят, но слот остался, и Spine ведёт его
 * за костью створки — надпись едет вместе с ней.
 *
 * Смещение снято из скелета: заготовка висит выше своей кости на 37.4. Ось Y у Spine
 * смотрит вверх, поэтому смещение положительное.
 */
export const REEL_COVER_CAPTION = { slot: 'title_cube_2', offsetY: 37.4 } as const;

/**
 * Место кнопки на нижней створке заглушки.
 *
 * Кнопка встаёт на вторую заготовку художника, слот title_cube_1. Он висит на кости нижней
 * створки, поэтому кнопка едет вместе с ней и стоит на своём месте без единой координаты
 * в игре, как и надпись выше.
 *
 * Заготовка стоит прямо на кости, смещения у неё нет.
 */
export const REEL_COVER_BUTTON = { slot: 'title_cube_1', offsetY: 0 } as const;

/**
 * Заглушка одного окна бонусного ряда: медная створка, которая уезжает вверх.
 *
 * Закрытая створка занимает 206x172 вокруг начала координат, то есть ровно окно с нахлёстом.
 * Анимации close_idle и open_idle в файле повторяют close, поэтому не объявлены:
 * закрытое состояние — это последний кадр close.
 */
export const BONUS_COVER_SKELETON = {
  skeletonUrl: '/animations/reels/jr_ss_reel_3x1/jr_ss_reel_3x1.json',
  atlasUrl: '/animations/reels/jr_ss_reel_3x1/jr_ss_reel_3x1.atlas',
  animations: {
    open: 'open',
    close: 'close',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Рамка ожидания: паровая труба с вентилем, которая обхватывает один барабан.
 *
 * Игра ставит её на третий барабан, когда два скаттера уже легли на первый и второй,
 * см. Anticipation в GDD, раздел 3.5. Труба падает на барабан, вентиль проворачивается,
 * и из четырёх коленьев бьёт пар, пока барабан не встанет.
 *
 * Имя файла у художника с опечаткой: anisipator вместо anticipator.
 *
 * Слоты title_cube… держат чёрные квадраты 122x114 — заготовки художника под четыре струи
 * пара. Каждый квадрат стоит ровно на кости своей струи, и снять его не может ни одна
 * анимация: ключей attachment у этих слотов нет нигде. Поэтому игра снимает их сама,
 * как и на заглушке поля, см. REEL_COVER_SKELETON.
 *
 * Слоты 00000 и reels_par пусты в скине: художник держал в них ленту барабана, пока собирал
 * сцену. Снимать их нечего, картинок в них нет.
 */
export const ANTICIPATION_SKELETON = {
  skeletonUrl: '/animations/anticipation/jr_ss_reels_anisipator.json',
  atlasUrl: '/animations/anticipation/jr_ss_reels_anisipator.atlas',
  animations: {
    intro: 'intro',
    loop: 'loop',
    out: 'out',
  },
  hiddenSlots: ['title_cube', 'title_cube2', 'title_cube3', 'title_cube4'],
} as const satisfies SpineSkeletonSource<string>;

/**
 * Проём рамки ожидания в координатах скелета: середина окна между трубами и его размер.
 *
 * Снято по сетке слота rama в позе по умолчанию. Сетка занимает 253x620 и лежит на -121.16..131.85
 * по горизонтали и на -343.78..276.23 по вертикали, то есть один в один с кадром атласа. Внутри
 * кадра проём занимает 24..228 и 23..537, отсюда и числа. Ось Y у Spine смотрит вверх, поэтому
 * середина проёма стоит ниже нуля.
 *
 * Проём нарисован под одну колонку поля 3x3: его ширина совпадает с окном барабана, а высота
 * чуть больше трёх рядов. По нему рамка и ставится на барабан, см. ui/anticipation.ts.
 */
export const ANTICIPATION_OPENING = { x: 4.84, y: -3.77, width: 204, height: 514 } as const;

/**
 * Стрелка манометра: она стоит на циферблате и показывает заряд от 0 до 10.
 *
 * Имена анимаций собираются по значению, а не перечисляются: их двадцать две, и все они
 * идут одним правилом, см. arrowIdleAnimation и arrowStepAnimation. Поэтому список анимаций
 * здесь пуст, а имена сверяет со скелетом свой тест.
 *
 * Слоты crack1, crack2 и crack3 держат треснувшее стекло: на восьмёрке загорается первая
 * трещина, на девятке вторая, на десятке третья. Включают их сами анимации стойки.
 *
 * Слоты wape и crack_mask держат пар и обрезку под то же стекло: они пойдут в дело
 * с перегревом манометра, см. Red Hot Spin в GDD, раздел 6.2. В позе по умолчанию оба
 * включены, и снять их не может ни одна анимация — ключей attachment у этих слотов нет нигде.
 * Поэтому пар лежит облаком поверх циферблата, а игра снимает оба слота сама.
 *
 * Порядок отрисовки игра задаёт свой: в скелете трещины лежат ниже стрелки, и стрелка идёт
 * поверх разбитого стекла. На деле стекло одно и закрывает весь циферблат, поэтому трещины
 * поднимаются над стрелкой.
 */
export const REEL_ARROW_SKELETON = {
  skeletonUrl: '/animations/reels/jr_ss_reel_arrow/jr_ss_reel_arrow.json',
  atlasUrl: '/animations/reels/jr_ss_reel_arrow/jr_ss_reel_arrow.atlas',
  animations: {},
  hiddenSlots: ['crack_mask', 'wape'],
  drawOrder: ['arrow', 'crack_mask', 'crack1', 'crack2', 'crack3', 'wape'],
} as const satisfies SpineSkeletonSource<string>;

/** Стойка стрелки манометра на заданном значении. */
export function arrowIdleAnimation(value: number): string {
  return `${value}_idle`;
}

/**
 * Ход стрелки манометра на заданное значение с соседнего деления слева.
 *
 * Ходов вниз у скелета нет, и по правилам игры они не нужны: заряд манометра только растёт,
 * см. GDD, раздел 4.2. Прыжок через несколько делений играется цепочкой таких ходов,
 * см. features/steamSpin/gauges.ts.
 */
export function arrowStepAnimation(to: number): string {
  return `${to - 1}-${to}`;
}

/**
 * Ход стрелки манометра обратно в ноль.
 *
 * Он один на весь скелет и нарисован с потолка шкалы: сбрасывать манометры игре приходится
 * только после бонуса Steam Spin, а туда игра попадает как раз с потолка, см. GDD, раздел 5.4.
 * Манометр, стоящий ниже, встаёт в ноль без хода: ходов вниз с прочих делений в ассете нет.
 */
export function arrowResetAnimation(max: number): string {
  return `${max}-0`;
}

/**
 * Панель общего множителя: перекидной счётчик и знак умножения в круге слева от него.
 *
 * Панель показывает сумму трёх манометров — тот множитель, на который бонус Steam Spin
 * умножает выигрыш мини-слота, см. GDD, разделы 5.2 и 5.3.
 *
 * Анимаций в скелете две. Animation перекидывает карточку счётчика и разом зажигает знак
 * умножения, idle — поза покоя одним кадром.
 *
 * Скелет нарисован в тех же пикселях, что и картинка барабанов, поэтому масштаб ему не нужен.
 */
export const TOTAL_METER_SKELETON = {
  skeletonUrl: '/animations/multiplier/jr_ss_meter.json',
  atlasUrl: '/animations/multiplier/jr_ss_meter.atlas',
  animations: {
    flip: 'animation',
    idle: 'idle',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Половинки карточек счётчика: той, что видна сейчас, и той, что выезжает ей на смену.
 *
 * Карточка разрезана надвое, и половинки лежат в своих слотах: их и перекидывает анимация.
 * Обе половинки одной карточки берут картинку одного числа, поэтому число им ставится одно.
 */
export const TOTAL_METER_CARDS = {
  current: ['number_current_1', 'number_current_2'],
  next: ['number_next_1', 'number_next_2'],
} as const;

/**
 * Карточка счётчика в координатах скелета: её размер и место разреза.
 *
 * Снято по сеткам половинок: вместе они занимают 114.6..235 по горизонтали и -83.6..72.3
 * по вертикали, а разрез идёт по 1.9, то есть чуть выше середины. Число рисуется под этот
 * размер и серединой на разрезе: иначе анимация резала бы цифры не там, где надо.
 */
export const TOTAL_METER_CARD = { width: 120, height: 156, split: 0.451 } as const;

/**
 * Место панели в координатах картинки барабанов.
 *
 * Панель прилегает к машине справа и стоит серединой на бонусном ряду, как на референсе
 * в GDD, раздел 5.2. По бонусному ряду машина шире всего на 1354: это край котла, снятый
 * по пикселям с альфой от 128 на высоте 541. Панель шириной 290 встаёт левым краем на него
 * с нахлёстом в 8 пикселей, чтобы кронштейн лёг на рамку, а не повис рядом с ней.
 */
export const TOTAL_METER_PLACE = { centerX: 1491, centerY: REELS_GRID.bonus.centerY } as const;

/**
 * Дорожка выигрышной линии: по полю слева направо пробегает железнодорожный путь.
 *
 * Скелет нарисован в координатах картинки барабанов: начало координат стоит у левого края
 * поля на середине ряда, а сама дорожка тянется на все три окна. Проверено по габариту
 * в заголовке скелета: он снят с той же картинки 1920x1920, что и сетка окон.
 *
 * Форм нарисовано три: прямая линия, диагональ через всё поле вниз и галка на ряд вверх.
 * Линию, которая идёт в другую сторону, игра отражает по вертикали, см. ui/winShow.ts.
 */
export const WINLINE_SKELETON = {
  skeletonUrl: '/animations/win/jr_ss_winline.json',
  atlasUrl: '/animations/win/jr_ss_winline.atlas',
  animations: {
    horizontal: 'horizontal',
    diagonal: 'diagonal',
    zigZag: 'zig_zag',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Крупный выигрыш: большой манометр на плите с двумя надписями, см. ui/bigWin.ts.
 *
 * Анимаций в скелете шесть, парами: у каждой ступени выигрыша свой влёт плиты и своя стойка.
 * Ступеней три — big win, mega win, super mega win, — но порогов у них ни в GDD, ни в описании
 * игры нет: слот знает один порог, см. ADR-24. Поэтому объявлена первая пара, а остальные
 * ждут своих порогов.
 *
 * Влёт идёт секунду: плита вылетает снизу, разрастается и садится на место. Стойка крутится
 * по кругу: бьётся стрелка манометра, трещит стекло и ходят блики.
 *
 * Слоты title_text и win_value держат чёрный квадрат 122x114 — заготовку художника под надпись
 * и сумму, как и на створках поля, см. REEL_COVER_SKELETON. Квадраты игра снимает, а надписи
 * ставит сама, см. BIG_WIN_LABELS.
 */
export const BIG_WIN_SKELETON = {
  skeletonUrl: '/animations/win/jr_ss_bw.json',
  atlasUrl: '/animations/win/jr_ss_bw.atlas',
  animations: {
    intro: '01_bigwin_intro',
    loop: '01_bigwin_loop',
  },
  hiddenSlots: ['title_text', 'win_value'],
} as const satisfies SpineSkeletonSource<string>;

/**
 * Рисунок Big Win в координатах скелета: середина по вертикали и высота.
 *
 * Габарит в заголовке скелета — это холст художника 1920x1920, по нему рисунок не поставить.
 * Снято с позы покоя самим рантаймом: рисунок занимает -541..636 по горизонтали и -752..317
 * по вертикали, то есть 1177x1069, и стоит он ниже начала координат. По высоте рисунок
 * и вписывается в экран.
 *
 * Сама плита занимает в нём 860x852 посередине. Остальное — белые полосы света под нижней
 * плитой: они шире её и уходят ниже. Мерить по одной плите нельзя, иначе полосы вылезли бы
 * за нижний край экрана.
 */
export const BIG_WIN_ART = { centerY: -217.3, height: 1069 } as const;

/**
 * Кремовое поле плиты Big Win в тех же координатах: в него встают надпись и сумма.
 *
 * Плита нарисована 860 в ширину, и медная рамка занимает на ней края. Надпись длиннее поля
 * ужимается, см. ui/label.ts.
 */
export const BIG_WIN_PLATE = { width: 800, height: 163 } as const;

/**
 * Места надписи и суммы на плите Big Win в координатах скелета.
 *
 * Обе заготовки висят на кости плиты, а кость эта повёрнута на девяносто градусов и уменьшена.
 * Узел слота Spine ведёт костью целиком, поэтому надпись на таком слоте встала бы боком.
 * Игра ставит надписи своим узлом, по этим координатам, см. ui/bigWin.ts. Ось Y тут смотрит
 * вверх, как и везде в Spine: надпись стоит над манометром, сумма под ним.
 */
export const BIG_WIN_LABELS = {
  caption: { x: -6.8, y: 214.3 },
  amount: { x: -1.3, y: -414.4 },
} as const;

/**
 * Клуб дыма из-под паровоза. Лежит среди фоновых ассетов, отсюда и адрес.
 *
 * Скелет перебирает раскадровку из сорока восьми картинок. Анимация full проходит её
 * с первого кадра до последнего: дым появляется и рассеивается. Анимация idle начинает
 * с шестого кадра и добавляет лёгкое сжатие, игре она не нужна.
 *
 * Клуб нарисован вокруг начала координат: его габарит 213x200 симметричен.
 * В той же папке лежат быстрый клуб, струя и верхний дым — их черёд придёт с другими событиями.
 *
 * Сборная сцена с паровозом (jj_ss_bg_slow_gauges) ставит этот же клуб парой: второй такой же,
 * отражённый по горизонтали. Игра повторяет эту пару, см. ui/smoke.ts.
 */
export const SMOKE_SKELETON = {
  skeletonUrl: '/animations/backgrounds/steam/jj_ss_bg_slow_bot/jj_ss_bg_slow_bot.json',
  atlasUrl: '/animations/backgrounds/steam/jj_ss_bg_slow_bot/jj_ss_bg_slow_bot.atlas',
  animations: {
    full: 'full',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Клуб дыма из-под паровоза на полном ходу.
 *
 * Тот же клуб, что и в покое, только гуще и быстрее: художник нарисовал его отдельной
 * раскадровкой под идущий поезд. Игра ставит его на то же место и теми же правилами,
 * см. ui/smoke.ts, а пускает на время поездки, см. ui/ride.ts.
 */
export const RUSH_SMOKE_SKELETON = {
  skeletonUrl: '/animations/backgrounds/steam/jj_ss_bg_fast_bot/jj_ss_bg_fast_bot.json',
  atlasUrl: '/animations/backgrounds/steam/jj_ss_bg_fast_bot/jj_ss_bg_fast_bot.atlas',
  animations: {
    full: 'full',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Струя пара из крана машины.
 *
 * Раскадровка идёт от первого кадра до последнего одну секунду с третью: струя бьёт, редеет
 * и растворяется. Пускают её открытая створка мини-слота и начало бонуса, см. ui/steam.ts.
 *
 * Нарисована струя стоймя, вверх от начала координат: её габарит 79x248 лежит на -48..31
 * по горизонтали и на -90.5..157.5 по вертикали. Значит, начало координат стоит не в основании
 * струи, а на трети её высоты, и по нему струя ставится на кран.
 */
export const STEAM_JET_SKELETON = {
  skeletonUrl: '/animations/backgrounds/steam/jj_ss_bg_flow/jj_ss_bg_flow.json',
  atlasUrl: '/animations/backgrounds/steam/jj_ss_bg_flow/jj_ss_bg_flow.atlas',
  animations: {
    jet: 'idle',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Завеса пара под манометрами. Она стоит всё время бонуса, см. ui/steam.ts.
 *
 * Раскадровка идёт три с половиной секунды и сходится с собственным началом: художник свёл её
 * двумя слотами внахлёст — один к концу гаснет, второй набирает силу. Поэтому завесу можно
 * крутить по кругу, и стыка не видно.
 *
 * Нарисована она вокруг начала координат: габарит 209x341 симметричен.
 */
export const GAUGE_STEAM_SKELETON = {
  skeletonUrl: '/animations/backgrounds/steam/jj_ss_bg_slow_top/jj_ss_bg_slow_top.json',
  atlasUrl: '/animations/backgrounds/steam/jj_ss_bg_slow_top/jj_ss_bg_slow_top.atlas',
  animations: {
    roll: 'idle',
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Кран на машине, из которого бьёт пар: его место в координатах картинки барабанов.
 *
 * Кранов на машине два, слева и справа от манометров. Взят левый: пар уходит вверх-влево
 * и в бонусный ряд не заходит. Точка снята по пикселям — это тело вентиля под красным
 * маховиком. Выше и левее струя отрывается от машины и висит на фоне сама по себе.
 */
export const STEAM_VALVE = { x: 620, y: 730 } as const;

/**
 * Фон игры: каньон с путём, гора слева и станция справа.
 *
 * Скелет держит всю поездку разом, от стоянки до стоянки, см. ADR-51. В покое он показывает
 * картинку BG.jpg одним куском (still). Разгон (depart) уводит гору со станцией вдаль, меняет
 * BG.jpg на размытый BG1.jpg и пускает мимо машины землю. Ход (run) гонит её по кругу.
 * Прибытие (arrive) тормозит землю, вводит станцию справа и возвращает BG.jpg.
 *
 * Четырёх больших картинок в атласе скелета нет: художник отдал их отдельными файлами,
 * а в скелете оставил под них сетки с заглушкой. Игра одевает эти сетки сама, см. textures
 * и assets/manager.ts. Слоты mount и mount1 — это одна и та же гора: на ходу их две,
 * одна уходит вдаль, вторая набегает ей на смену.
 *
 * Остальное — мелочь из атласа скелета: камни, трава, столбы и рельсы, которые летят мимо.
 */
export const BACKGROUND_SKELETON = {
  skeletonUrl: '/animations/backgrounds/main/jr_ss_background.json',
  atlasUrl: '/animations/backgrounds/main/jr_ss_background.atlas',
  animations: {
    still: 'idle_static',
    depart: 'start',
    run: 'idle',
    arrive: 'stop',
  },
  textures: {
    BG: BACKGROUND_IMAGE,
    BG1: RUSH_BACKGROUND_IMAGE,
    mount: MOUNTAIN_IMAGE,
    mount1: MOUNTAIN_IMAGE,
    RST: STATION_IMAGE,
  },
} as const satisfies SpineSkeletonSource<string>;

/**
 * Слоты подмены на прибытии: нарисованная плита, размытая под ней и спрайт горы.
 *
 * Нарисованная плита встаёт непрозрачной за полсекунды до того, как спрайт станции доедет
 * до своего места, и эту полсекунды на экране стоят две станции. Гора к своему уходу уже
 * доехала: её ход кончается раньше, чем спрайт начинает таять. По этой кривой игра выводит
 * и плиту, см. ui/background.ts.
 */
export const BACKGROUND_SWAP = { plate: 'BG', blur: 'BG1', mountain: 'mount' } as const;

// Скелеты символов лежат каждый в своей папке, имя файла внутри папки совпадает с ней не всегда.
// Поэтому папка и имя файла задаются отдельно, а расширения дописывает symbolSkeleton.
const SYMBOL_ANIMATIONS = '/animations/symbols';

function symbolSkeleton<TAnimation extends string>(
  folder: string,
  file: string,
  animations: Readonly<Record<TAnimation, string>>,
): SpineSkeletonSource<TAnimation> {
  return {
    skeletonUrl: `${SYMBOL_ANIMATIONS}/${folder}/${file}.json`,
    atlasUrl: `${SYMBOL_ANIMATIONS}/${folder}/${file}.atlas`,
    animations,
  };
}

/** Анимация выигрыша символа. Имя у неё одно на все символы, см. ui/winShow.ts. */
export const SYMBOL_WIN_ANIMATION: string = 'win';

/** Анимация появления символа на барабанах. Её играет wild, попав на поле, см. ui/winShow.ts. */
export const SYMBOL_BLINK_ANIMATION: string = 'blink';

/**
 * Анимация выигрыша символа с меткой.
 *
 * Скелет Wild Multiplier рисует значение сам: у каждого значения своя анимация, и она зажигает
 * свою цифру на циферблате. Поэтому помеченная ячейка играет не общий win, а анимацию
 * своего значения, см. features/steamSpin/multipliers.ts.
 */
export function markedWinAnimation(value: number): string {
  return `win_${value}`;
}

/** Анимация появления символа с меткой. Её играет помеченная ячейка, которая не выиграла. */
export function markedBlinkAnimation(value: number): string {
  return `win_${value}_appearance`;
}

// У всех символов есть анимация выигрыша. По GDD (раздел 3.6) Wild и Bonus Scatter
// дополнительно мигают при попадании на барабаны, у них для этого есть blink.
const WIN = { win: SYMBOL_WIN_ANIMATION } as const;
const WIN_AND_BLINK = { win: SYMBOL_WIN_ANIMATION, blink: SYMBOL_BLINK_ANIMATION } as const;

/** Анимации золотых слитков. */
export const GOLD_BARS_SKELETON = symbolSkeleton('jr_ss_high_1_goldbars', 'jr_ss_high_1_goldbars', WIN);

/** Анимации колокола. */
export const BELL_SKELETON = symbolSkeleton('jr_ss_high_2_bell', 'jr_ss_high_2_bell', WIN);

/** Анимации фуражки кондуктора. */
export const HAT_SKELETON = symbolSkeleton('jr_ss_high_3_hat', 'jr_ss_high_3_hat', WIN);

/** Анимации чемодана. */
export const SUITCASE_SKELETON = symbolSkeleton('jr_ss_high_4_suitcase', 'jr_ss_high_4_suitcase', WIN);

/** Анимации вишни. */
export const CHERRY_SKELETON = symbolSkeleton('jr_ss_low_cherry', 'jr_ss_low_cherry', WIN);

/** Анимации лимона. */
export const LEMON_SKELETON = symbolSkeleton('jr_ss_low_lemon_cut', 'jr_ss_low_lemon', WIN);

/** Анимации груши. */
export const PEAR_SKELETON = symbolSkeleton('jr_ss_low_pear', 'jr_ss_low_pear', WIN);

/** Анимации сливы. */
export const PLUM_SKELETON = symbolSkeleton('jr_ss_low_plum', 'jr_ss_low_plum', WIN);

/** Анимации бонусного скаттера. */
export const SCATTER_SKELETON = symbolSkeleton('jr_ss_special_symbol', 'jr_ss_special_symbol', WIN_AND_BLINK);

/** Анимации Wild. */
export const WILD_SKELETON = symbolSkeleton('jr_ss_wild_final', 'jr_ss_wild', WIN_AND_BLINK);

/**
 * Анимации Wild Multiplier.
 *
 * Выигрышных анимаций одиннадцать, по одной на значение множителя. У каждой есть парная
 * anticipation-версия: она показывает появление множителя перед самим выигрышем.
 */
export const WILD_MULTIPLIER_SKELETON = symbolSkeleton('jr_ss_wild_multiplier', 'jr_ss_wild_multiplier', {
  blink: 'blink',
  idle: 'idle',
  win1: 'win_1',
  win1Appearance: 'win_1_appearance',
  win2: 'win_2',
  win2Appearance: 'win_2_appearance',
  win3: 'win_3',
  win3Appearance: 'win_3_appearance',
  win4: 'win_4',
  win4Appearance: 'win_4_appearance',
  win5: 'win_5',
  win5Appearance: 'win_5_appearance',
  win6: 'win_6',
  win6Appearance: 'win_6_appearance',
  win7: 'win_7',
  win7Appearance: 'win_7_appearance',
  win8: 'win_8',
  win8Appearance: 'win_8_appearance',
  win9: 'win_9',
  win9Appearance: 'win_9_appearance',
  win10: 'win_10',
  win10Appearance: 'win_10_appearance',
  win11: 'win_11',
  win11Appearance: 'win_11_appearance',
});

/** Ассеты одного символа. */
export interface SymbolAssets {
  /** Кадр на остановленном барабане. */
  readonly still: SymbolFrame;

  /** Кадр на крутящемся барабане. Null у символа, который в полосы барабанов не входит. */
  readonly blurred: BlurredSymbolFrame | null;

  /** Скелет символа: по нему играет анимация выигрыша. */
  readonly skeleton: SpineSkeletonSource<string>;
}

/**
 * Ассеты каждого символа игры. Ключ — идентификатор символа с бэкенда, см. GDD, раздел 2.
 *
 * Младшие символы в GDD показаны чужой временной графикой: арбуз, слива, апельсин, вишня.
 * В ассетах Steam Spin нарисованы вишня, лимон, груша, слива. Слива и вишня совпадают,
 * остальные две пары взяты по цвету и форме: арбуз (id_07) — груша, апельсин (id_09) — лимон.
 *
 * Wild Multiplier (id_01) на стадии 2 подменяет собой Wild и потому крутится на барабанах,
 * см. GDD, раздел 5.2. Своего размытого кадра у него нет: в листе лежит только кадр с буквой W,
 * тот же, что у обычного Wild. На ходу манометр от буквы всё равно не отличить, поэтому
 * оба символа берут его. Значение множителя игра рисует шрифтом поверх остановленного символа.
 */
export const SYMBOL_ASSETS = {
  id_00: { still: 'wild', blurred: 'wild', skeleton: WILD_SKELETON },
  id_01: { still: 'wildMultiplier', blurred: 'wild', skeleton: WILD_MULTIPLIER_SKELETON },
  id_02: { still: 'goldBars', blurred: 'goldBars', skeleton: GOLD_BARS_SKELETON },
  id_03: { still: 'hat', blurred: 'hat', skeleton: HAT_SKELETON },
  id_04: { still: 'bell', blurred: 'bell', skeleton: BELL_SKELETON },
  id_06: { still: 'suitcase', blurred: 'suitcase', skeleton: SUITCASE_SKELETON },
  id_07: { still: 'pear', blurred: 'pear', skeleton: PEAR_SKELETON },
  id_08: { still: 'plum', blurred: 'plum', skeleton: PLUM_SKELETON },
  id_09: { still: 'lemon', blurred: 'lemon', skeleton: LEMON_SKELETON },
  id_10: { still: 'cherry', blurred: 'cherry', skeleton: CHERRY_SKELETON },
  id_11: { still: 'scatter', blurred: 'scatter', skeleton: SCATTER_SKELETON },
} as const satisfies Readonly<Record<string, SymbolAssets>>;

/** Идентификаторы символов, для которых объявлены ассеты. */
export type MappedSymbolId = keyof typeof SYMBOL_ASSETS;

/** Скелеты всех символов. */
const SYMBOL_SKELETONS: readonly SpineSkeletonSource<string>[] = Object.values(SYMBOL_ASSETS).map((symbol) => symbol.skeleton);

/** Скелеты рамки барабанов: разделители, заглушки, рамка ожидания, стрелки манометров и счётчик. */
const REEL_SKELETONS: readonly SpineSkeletonSource<string>[] = [REEL_GEAR_SKELETON, REEL_COVER_SKELETON, BONUS_COVER_SKELETON, ANTICIPATION_SKELETON, REEL_ARROW_SKELETON, TOTAL_METER_SKELETON];

/** Скелеты пара: струя из крана машины и завеса под манометрами, см. ui/steam.ts. */
const STEAM_SKELETONS: readonly SpineSkeletonSource<string>[] = [STEAM_JET_SKELETON, GAUGE_STEAM_SKELETON];

/** Все объявленные скелеты. По этому списку тест проверяет, что файлы и анимации существуют. */
export const ALL_SPINE_SKELETONS: readonly SpineSkeletonSource<string>[] = [LOGO_SKELETON, WINLINE_SKELETON, BIG_WIN_SKELETON, BACKGROUND_SKELETON, SMOKE_SKELETON, RUSH_SMOKE_SKELETON, ...STEAM_SKELETONS, ...REEL_SKELETONS, ...SYMBOL_SKELETONS];

/** Ассеты экрана загрузки. Грузятся первыми: без них показывать прогресс не на чем. */
export const BOOT_BUNDLE: AssetBundle = { sheets: [PRELOADER_SHEET], skeletons: [LOGO_SKELETON] };

/** Ассеты экрана интро и игрового поля, вместе со всеми символами. Грузятся под экраном загрузки. */
export const GAME_BUNDLE: AssetBundle = {
  images: ALL_IMAGES,
  sheets: [SYMBOLS_SHEET, BLURRED_SYMBOLS_SHEET, UI_SHEET, MISC_UI_SHEET, PAGER_SHEET, WIN_FONT_SHEET],
  skeletons: [WINLINE_SKELETON, BIG_WIN_SKELETON, BACKGROUND_SKELETON, SMOKE_SKELETON, RUSH_SMOKE_SKELETON, ...STEAM_SKELETONS, ...REEL_SKELETONS, ...SYMBOL_SKELETONS],
  fonts: ALL_FONTS,
  sounds: ALL_SOUND_SPRITES,
};
